/** Paces streamed text so it renders as a steady character-by-character flow
 * instead of stuttering in network-sized chunks.
 *
 * Incoming text is buffered and drained on a ~60fps tick, one character per
 * tick at normal model speed. When the model outruns the display the per-tick
 * emission scales with the backlog (targeting a drain over ~CATCHUP_FRAMES
 * ticks), so the display stays smooth without falling ever further behind.
 *
 * flush() keeps the smooth drain going and resolves once the buffer is empty;
 * after FLUSH_MAX_MS it dumps the remainder in one emit — background tabs
 * throttle timers to ~1s, and finalization must not hang on an invisible
 * animation. cancel() drops whatever is still buffered (the caller is
 * discarding the message anyway).
 */

export interface StreamSmoother {
  push(text: string): void
  flush(): Promise<void>
  cancel(): void
}

const FRAME_MS = 16
const CATCHUP_FRAMES = 45
const FLUSH_MAX_MS = 2000

export function createStreamSmoother(
  emit: (chunk: string) => void
): StreamSmoother {
  let buffer = ""
  let timer: ReturnType<typeof setTimeout> | null = null
  let flushResolvers: Array<() => void> = []
  let flushStartedAt: number | null = null

  const settleFlush = () => {
    const resolvers = flushResolvers
    flushResolvers = []
    flushStartedAt = null
    resolvers.forEach((resolve) => resolve())
  }

  const tick = () => {
    timer = null
    if (buffer) {
      if (flushStartedAt !== null && Date.now() - flushStartedAt >= FLUSH_MAX_MS) {
        emit(buffer)
        buffer = ""
      } else {
        let n = Math.max(1, Math.ceil(buffer.length / CATCHUP_FRAMES))
        // Don't split a surrogate pair across ticks (emoji would flicker as
        // a replacement character for a frame).
        const last = buffer.charCodeAt(n - 1)
        if (n < buffer.length && last >= 0xd800 && last <= 0xdbff) n += 1
        emit(buffer.slice(0, n))
        buffer = buffer.slice(n)
      }
    }
    if (buffer) {
      timer = setTimeout(tick, FRAME_MS)
    } else {
      settleFlush()
    }
  }

  const ensureTicking = () => {
    if (timer === null) timer = setTimeout(tick, FRAME_MS)
  }

  return {
    push(text) {
      if (!text) return
      buffer += text
      ensureTicking()
    },
    flush() {
      if (!buffer && timer === null) return Promise.resolve()
      if (flushStartedAt === null) flushStartedAt = Date.now()
      return new Promise((resolve) => {
        flushResolvers.push(resolve)
        ensureTicking()
      })
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      buffer = ""
      settleFlush()
    },
  }
}
